import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Divider, CircularProgress } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams, useNavigate } from 'react-router-dom';

import iconLeftInspection from '../../../assets/images/icon_left_inspection.svg';
import Loading from '../../components/Loading';
import { formatLicensePlate } from '../../helpers/formatLicensePlate';
import { ROUTES } from '../constants/routes';
import VehicleInspections from '../api/vehicle_inspections';

const MyInspection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);

  // preferir placa do payload; cair para slice do id se necessário
  const licensePlateFromId = id?.slice(-7);
  const licensePlate =
    inspection?.vehicle_plate || (licensePlateFromId ? formatLicensePlate(licensePlateFromId) : '');

  const pollingRef = useRef(null);

  const fetchInspection = async () => {
    try {
      const resp = await VehicleInspections.getById(id);
      // backend pode responder { inspection: {...} } ou {...}
      const payload = resp?.inspection ?? resp;
      setInspection(payload);
      return payload;
    } catch (error) {
      console.error('Erro ao buscar detalhes da inspeção:', error);
      navigate(ROUTES.MY_INSPECTIONS, {
        replace: true,
        state: { error: 'Erro ao carregar dados da inspeção' }
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchInspection();
  }, [id, navigate]);

  // Polling quando está pago (ou consulta via crédito B2B) mas sem laudo ainda
  useEffect(() => {
    const isPaid =
      inspection?.status === 'paid' ||
      inspection?.plan_type === 'package' ||
      ['processing', 'ready'].includes(inspection?.inspection_status);
    const hasReport = Boolean(inspection?.report_file_url);
    const shouldPoll = isPaid && !hasReport;

    if (shouldPoll) {
      pollingRef.current = setInterval(async () => {
        const updated = await fetchInspection();
        if (updated?.report_file_url) {
          clearInterval(pollingRef.current);
        }
      }, 30000); // 30 segundos
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [inspection?.status, inspection?.plan_type, inspection?.inspection_status, inspection?.report_file_url]);

  const handleDownload = () => {
    if (!inspection?.report_file_url) {
      console.error('URL do laudo não disponível');
      return;
    }
    window.open(inspection.report_file_url, '_blank');
  };

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: '#F5F7F9',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Loading />
      </Box>
    );
  }

  if (!id) return null;

  const isPaid =
    inspection?.status === 'paid' ||
    inspection?.plan_type === 'package' ||
    ['processing', 'ready'].includes(inspection?.inspection_status);
  const hasReport = Boolean(inspection?.report_file_url);
  const isPaidWithoutReport = isPaid && !hasReport;
  const showProceedButton = Boolean(inspection) && !isPaid;

  if (isPaidWithoutReport) {
    return (
      <Box sx={{
        bgcolor: '#F5F7F9',
        pt: { xs: 8, sm: 9, md: 10 },
        pb: { xs: 4, sm: 5, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        minHeight: '100vh',
      }}>
        <Box sx={{
          maxWidth: { xs: '100%', lg: '1000px' },
          mx: 'auto',
          bgcolor: '#FFFFFF',
          borderRadius: { xs: 2, sm: 3, md: 4 },
          boxShadow: '0px 6px 24px 0px #012F473A',
          p: { xs: 4, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3,
          mb: 4,
        }}>
          <Box sx={{ position: 'relative' }}>
            <CircularProgress
              sx={{
                width: { xs: '64px', md: '80px' },
                height: { xs: '64px', md: '80px' },
                color: '#51DF94',
              }}
            />
          </Box>

          <Typography
            variant="h1"
            sx={{
              color: '#012F47',
              fontWeight: 700,
              fontSize: { xs: '24px', md: '32px' },
            }}
          >
            Vistoria em processamento
          </Typography>

          <Typography
            sx={{
              color: '#536777',
              fontSize: { xs: '14px', md: '16px' },
              maxWidth: '600px',
              lineHeight: 1.5,
              fontWeight: 400,
            }}
          >
            Sua vistoria está sendo gerada e em breve estará disponível.
            {' '}
            Você receberá um e-mail assim que o documento estiver disponível.
            Esta página será atualizada automaticamente quando o laudo estiver pronto.
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate(ROUTES.MY_INSPECTIONS)}
            sx={{
              mt: 2,
              bgcolor: '#012F47',
              '&:hover': { bgcolor: '#011F30' },
              textTransform: 'none',
              px: { xs: 3, md: 4 },
              py: { xs: 1, md: 1.5 },
              fontSize: { xs: '0.875rem', md: '1rem' },
              borderRadius: '8px'
            }}
          >
            Voltar para minhas vistorias
          </Button>
        </Box>
      </Box>
    );
  }

  const CardTitle = ({ children }) => (
    <Typography
      sx={{
        position: 'absolute',
        top: '-13px',
        left: '24px',
        color: '#51DF94',
        fontSize: '1.1rem',
        fontWeight: 600,
        bgcolor: 'white',
        px: 1,
        lineHeight: 1
      }}
    >
      {children}
    </Typography>
  );

  const getConsistentLength = (label) =>
    (label.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 7) + 6;

  const BlurredValue = ({ label }) => (
    <Typography
      sx={{
        color: '#012F47',
        fontSize: '0.875rem',
        fontWeight: 500,
        filter: 'blur(4px)',
        userSelect: 'none'
      }}
    >
      {'*'.repeat(getConsistentLength(label))}
    </Typography>
  );

  const NormalValue = ({ children }) => (
    <Typography sx={{ color: '#012F47', fontSize: '0.875rem', fontWeight: 500 }}>
      {children}
    </Typography>
  );

  const PROCEED_TO_PAYMENT_LABEL =
    'Prosseguir para pagamento e acessar laudo completo';

  const PdfPreview = ({ src }) => (
    <Box
      sx={{
        width: '100%',
        height: { xs: '520px', sm: '640px', md: '820px' },
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 12px 35px rgba(1, 47, 71, 0.18)',
        bgcolor: '#F5F5F5',
        border: '1px solid rgba(1, 47, 71, 0.08)'
      }}
    >
      <Box
        component="iframe"
        src={src}
        title="Prévia do laudo em PDF"
        loading="lazy"
        sx={{
          border: 'none',
          width: '100%',
          height: '100%',
          bgcolor: '#F5F5F5'
        }}
      />
    </Box>
  );

  const BlurredTitle = () => (
    <Typography
      sx={{
        color: '#51DF94',
        fontSize: '1.45rem',
        fontWeight: 600,
        textAlign: 'center',
        filter: 'blur(4px)',
        userSelect: 'none',
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.02)'
        }
      }}
    >
      Decodificação de Chassi
    </Typography>
  );

  const InfoRow = ({ label, value, isPlaca, topSpacing }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 1.5, sm: 2, md: 2.5 },
        ...(topSpacing && { mt: { xs: 2.5, sm: 3, md: 4 } }),
        transition: 'all 0.2s ease',
        p: { xs: 1, sm: 1.25 },
        borderRadius: '6px',
        '&:hover': {
          transform: 'translateX(4px)',
          bgcolor: 'rgba(0, 178, 108, 0.03)'
        }
      }}
    >
      <Typography sx={{ color: '#012F47', fontSize: '0.875rem', opacity: 0.7 }}>{label}</Typography>
      <Box sx={{ flex: 1, borderBottom: '1px solid #012F47', mx: 2, opacity: 0.3 }} />
      {isPlaca ? <NormalValue>{value || '-'}</NormalValue> : <BlurredValue label={label} />}
    </Box>
  );

  const vehicleInfoLeft = [
    { label: 'Placa', value: formatLicensePlate(licensePlate) },
    { label: 'Nº do documento faturado', value: '123456' },
    { label: 'UF de faturamento', value: 'Lorem Ipsum' },
    { label: 'Tipo de carroceria', value: 'ABCDEF' },
    { label: 'Situação do chassi', value: 'KIQ4779', topSpacing: true },
    { label: 'Município', value: '123456' },
    { label: 'Cor', value: 'Lorem Ipsum' },
    { label: 'Nº do registro DI', value: 'ABCDEF' },
    { label: 'Última atualização', value: 'KIQ4779', topSpacing: true },
    { label: 'Capacidade máxima de tração', value: '123456' },
    { label: 'Modelo', value: 'Lorem Ipsum' },
    { label: 'Ano do modelo', value: 'ABCDEF' },
    { label: 'Chassi', value: 'KIQ4779', topSpacing: true },
    { label: 'Renavam', value: '123456' },
    { label: 'Situação do veículo', value: 'Lorem Ipsum' },
    { label: 'UF', value: 'ABCDEF' }
  ];

  const vehicleInfoRight = [
    { label: 'Marca', value: 'KIQ4779' },
    { label: 'Capacidade de carga', value: '123456' },
    { label: 'Categoria', value: 'Lorem Ipsum' },
    { label: 'Remarcação do chassi', value: 'ABCDEF' },
    { label: 'Nº atual do motor', value: 'KIQ4779', topSpacing: true },
    { label: 'Cilindradas', value: '123456' },
    { label: 'Nº da caixa de câmbio', value: 'Lorem Ipsum' },
    { label: 'Peso bruto total', value: 'ABCDEF' },
    { label: 'Nº de eixos', value: 'KIQ4779', topSpacing: true },
    { label: 'Capacidade de passageiros', value: '123456' },
    { label: 'Procedência', value: 'Lorem Ipsum' },
    { label: 'Potência', value: 'ABCDEF' },
    { label: 'Combustível', value: 'KIQ4779', topSpacing: true },
    { label: 'Tipo de veículo', value: '123456' }
  ];

  const chassiDecodingLeft = [
    { label: 'Portas', value: 'KIQ4779' },
    { label: 'Motor', value: '123456' },
    { label: 'Verificação serial', value: 'Lorem Ipsum' },
    { label: 'Tração', value: '123456' },
    { label: 'Versão', value: 'Lorem ipsum' }
  ];

  const chassiDecodingRight = [
    { label: 'Conclusão', value: 'KIQ4779' },
    { label: 'Fabricante', value: '123456' },
    { label: 'Localização da fábrica', value: 'Lorem Ipsum' },
    { label: 'N° de irregularidades', value: '123456' },
    { label: 'Região', value: 'Lorem ipsum' }
  ];

  const renajudLeft = [
    { label: 'Data limite', value: 'KIQ4779' },
    { label: 'CMT', value: '123456' },
    { label: 'Data limite', value: 'Lorem Ipsum' }
  ];

  const renajudRight = [
    { label: 'Montagem', value: 'KIQ4779' },
    { label: 'PTB', value: '123456' },
    { label: 'Remarcação do chassi', value: 'Lorem Ipsum' }
  ];

  const handleNavigateNext = () => {
    // const target = inspection?.plan_type === 'premium' ? ROUTES.SCHEDULE : ROUTES.PAYMENT;
    const target = ROUTES.PAYMENT;

    navigate(`${target}/${id}`, {
      state: {
        licensePlate: licensePlate,
        price: inspection?.plan_value,
        planType: inspection?.plan_type
      }
    });
  };

  const InfoCard = ({ title, leftContent, rightContent, showOverlay = true }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
      if (showOverlay) setIsHovered(true);
    };

    const handleMouseLeave = () => {
      if (showOverlay) setIsHovered(false);
    };

    return (
      <Box
        sx={{
          border: '2.56px solid #51DF94',
          borderRadius: { xs: '16px', sm: '20px' },
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 4, sm: 5, md: 6 },
          position: 'relative',
          mt: { xs: 4, sm: 5, md: 6 },
          transition: 'all 0.3s ease',
          bgcolor: 'white',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 178, 108, 0.15)',
            transform: 'translateY(-4px)'
          }
        }}
      >
        <CardTitle>{title}</CardTitle>

        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ position: 'relative', cursor: 'default' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3, md: 4 },
              filter: isHovered && showOverlay ? 'blur(2px)' : 'none',
              transition: 'filter 0.3s ease'
            }}
          >
            <Box sx={{ flex: 1, width: '100%', px: { xs: 1, sm: 1.5 } }}>{leftContent}</Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: '#012F47',
                borderRightWidth: 1.5,
                mx: { sm: 2, lg: 4 },
                opacity: 0.3,
                display: { xs: 'none', sm: 'block' }
              }}
            />

            <Box sx={{ flex: 1, width: '100%', px: { xs: 1, sm: 1.5 } }}>{rightContent}</Box>
          </Box>

          {showOverlay && isHovered && (
            <Button
              variant="contained"
              onClick={handleNavigateNext}
              aria-label={PROCEED_TO_PAYMENT_LABEL}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: '#51DF94',
                color: 'white',
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 1.25, sm: 1.5, md: 2 },
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 178, 108, 0.25)',
                zIndex: 2,
                '&:hover': {
                  bgcolor: '#00B26C',
                  transform: 'translate(-50%, -50%) translateY(-2px)'
                },
                transition: 'all 0.3s ease',
                whiteSpace: 'normal',
                textAlign: 'center',
                lineHeight: 1.35,
                maxWidth: { xs: '85%', sm: '75%', md: '90%' }
              }}
            >
              {PROCEED_TO_PAYMENT_LABEL}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: '#F5F7F9',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: { xs: 3, sm: 4, md: 5, lg: 6 }
      }}
    >
      {inspection?.status === 'paid' && inspection?.report_file_url ? (
        <Box
          sx={{
            maxWidth: { xs: '100%', lg: '1000px' },
            mx: 'auto',
            width: '100%',
            bgcolor: '#FFFFFF',
            borderRadius: { xs: 2, sm: 3, md: 4 },
            boxShadow: '0px 6px 24px 0px #012F473A',
            p: { xs: 4, md: 6 }
          }}
        >
          {inspection && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, sm: 4 } }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    bgcolor: '#51DF94',
                    '&:hover': {
                      bgcolor: '#00B26C',
                      transform: 'translateY(-2px)',
                      transition: 'transform 0.2s ease-in-out'
                    },
                    borderRadius: '8px',
                    textTransform: 'none',
                    px: { xs: 2, sm: 3, md: 4 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    boxShadow: '0 4px 15px rgba(0, 178, 108, 0.25)'
                  }}
                >
                  Baixar laudo
                </Button>
              </Box>

              <PdfPreview src={inspection.report_file_url} />

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, sm: 5 } }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    bgcolor: '#51DF94',
                    '&:hover': {
                      bgcolor: '#00B26C',
                      transform: 'translateY(-2px)',
                      transition: 'transform 0.2s ease-in-out'
                    },
                    borderRadius: '8px',
                    textTransform: 'none',
                    px: { xs: 2, sm: 3, md: 4 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    boxShadow: '0 4px 15px rgba(0, 178, 108, 0.25)'
                  }}
                >
                  Baixar laudo
                </Button>
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: { xs: '16px', sm: '20px', md: '24px' },
            boxShadow: '0 12px 35px rgba(1, 47, 71, 0.1)',
            width: '100%',
            maxWidth: { xs: '95%', sm: '92%', md: '88%', lg: '1200px' },
            mx: 'auto',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {inspection && showProceedButton && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNavigateNext}
              sx={{
                position: 'absolute',
                right: { xs: '16px', sm: '24px', md: '32px' },
                top: { xs: '16px', sm: '24px', md: '32px' },
                zIndex: 10,
                bgcolor: '#51DF94',
                color: 'white',
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.9rem' },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.75, sm: 1, md: 1.25 },
                '&:hover': { bgcolor: '#00B26C', transform: 'translateY(-2px)' },
                borderRadius: { xs: '6px', sm: '8px' },
                boxShadow: '0 4px 15px rgba(0, 178, 108, 0.25)',
                whiteSpace: 'nowrap',
                minWidth: { xs: '100px', sm: '110px', md: '120px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 0.5, sm: 0.75 }
              }}
            >
              Prosseguir
            </Button>
          )}

          <Box
            sx={{
              bgcolor: '#012F47',
              p: { xs: 2, sm: 3, md: 4 },
              position: 'relative',
              transform: 'rotate(-2.9deg)',
              transformOrigin: 'center',
              marginTop: { xs: '-28px', sm: '-35px', md: '-40px' },
              marginBottom: { xs: '-36px', sm: '-45px', md: '-50px' },
              paddingTop: { xs: '56px', sm: '70px', md: '60px' },
              paddingBottom: { xs: '28px', sm: '35px', md: '30px' },
              width: '120%',
              marginLeft: '-10%'
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                transform: 'rotate(2.9deg)',
                mt: { xs: 4, sm: 5, md: 6 },
                pl: { xs: '60px', sm: '70px', md: '80px' }
              }}
            >
              <Box
                component="img"
                src={iconLeftInspection}
                alt="Ícone de inspeção"
                sx={{
                  position: 'absolute',
                  left: { xs: '20px', sm: '23px', md: '38px', lg: '77px' },
                  top: { xs: '52px', sm: '107px', md: '150px' },
                  transform: 'translateY(-50%)',
                  width: { xs: '88px', sm: '150px', md: '200px' },
                  height: 'auto'
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' },
                  textAlign: 'center',
                  pr: { xs: '40px', sm: '60px', md: '100px' },
                  lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
                  pt: { xs: 1, sm: 2, md: 0 }
                }}
              >
                Laudo de vistoria cautelar
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{ 
              p: { xs: 3, sm: 4, md: 5 }, 
              mt: { xs: 8, sm: 10, md: 12 },
              mx: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <InfoCard
              title="Veículo"
              leftContent={vehicleInfoLeft.map((item, index) => (
                <InfoRow
                  key={index}
                  label={item.label}
                  value={item.value}
                  isPlaca={item.label === 'Placa'}
                  topSpacing={item.topSpacing}
                />
              ))}
              rightContent={vehicleInfoRight.map((item, index) => (
                <InfoRow
                  key={index}
                  label={item.label}
                  value={item.value}
                  isPlaca={item.label === 'Placa'}
                  topSpacing={item.topSpacing}
                />
              ))}
              showOverlay={showProceedButton}
            />

            <InfoCard
              title="Renajud"
              leftContent={renajudLeft.map((item, index) => (
                <InfoRow
                  key={index}
                  label={item.label}
                  value={item.value}
                  isPlaca={item.label === 'Placa'}
                  topSpacing={item.topSpacing}
                />
              ))}
              rightContent={renajudRight.map((item, index) => (
                <InfoRow
                  key={index}
                  label={item.label}
                  value={item.value}
                  isPlaca={item.label === 'Placa'}
                  topSpacing={item.topSpacing}
                />
              ))}
              showOverlay={showProceedButton}
            />

            <InfoCard
              title="Decodificação de Chassi"
              leftContent={chassiDecodingLeft.map((item, index) => (
                <InfoRow
                  key={index}
                  label={item.label}
                  value={item.value}
                  isPlaca={item.label === 'Placa'}
                  topSpacing={item.topSpacing}
                />
              ))}
              rightContent={chassiDecodingRight.map((item, index) => (
                <InfoRow
                  key={index}
                  label={item.label}
                  value={item.value}
                  isPlaca={item.label === 'Placa'}
                  topSpacing={item.topSpacing}
                />
              ))}
              showOverlay={showProceedButton}
            />

            <Box
              sx={{
                display: 'flex',
                mt: 8,
                gap: 4
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                <BlurredTitle />
                <BlurredTitle />
                <BlurredTitle />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                <BlurredTitle />
                <BlurredTitle />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MyInspection;
